import useApi, {
  SearchParams,
  useApiMutation,
  UseApiMutationResult,
  UseApiResult,
} from 'hooks/useApi';
import useDownload from 'hooks/useDownload';
import { t } from 'i18next';
import { useMutation } from 'react-query';
import BaseService from 'services/api/BaseService';
import { DossiersUrls } from 'services/api/urls';
import {
  CalculPrimeConcessionnaireParams,
  CalculPrimeParticulierConsessionnaireParams,
  ClientType,
} from 'types/Concessionnaire';
import {
  CreateDossierLead,
  DepartmentCodeResponse,
  DossierDetails,
  Dossiers,
  DossiersSearchParams,
  Personne,
} from 'types/Dossier';
import {
  OperationDetails,
  Operations,
  OperationsSearchParams,
} from 'types/Operation';
import { ViewMode } from '../components';
import { Company } from '../types/Company';

const NO_PRIME_CONCESSIONNAIRE_MESSAGE =
  'Aucune prime concessionnaire ne correspond aux critères fournis.';
const NO_PRIME_AVAILABLE_FORM_MESSAGE =
  'Aucune prime disponible pour les données saisies.';

function getCalculPrimeConcessionnaireErrorMessage(body?: {
  message?: string;
  statusCode?: number;
}) {
  if (
    body?.statusCode === 400 &&
    body.message === NO_PRIME_CONCESSIONNAIRE_MESSAGE
  ) {
    return NO_PRIME_AVAILABLE_FORM_MESSAGE;
  }

  return body?.message || 'Erreur lors du calcul de la prime et du volume';
}

export function useCreateDossierMutation(): UseApiMutationResult<
  CreateDossierLead,
  { id: string }
> {
  return useApiMutation<CreateDossierLead, { id: string }>(DossiersUrls.ADD, {
    method: 'POST',
    isJson: false,
  });
}

export function useDossiers(
  searchParams: DossiersSearchParams,
  viewMode?: ViewMode,
): UseApiResult<Dossiers> {
  return useApi<Dossiers>(DossiersUrls.LIST, {
    searchParams: searchParams as SearchParams,
    key: [viewMode, searchParams],
  });
}

export function useReferentsCommerciaux(
  searchParams: {
    statuts?: string[];
  } = {},
): UseApiResult<Personne[]> {
  return useApi<Personne[]>(DossiersUrls.GET_REFERENTS_COMMERCIAUX, {
    searchParams: searchParams as SearchParams,
  });
}

export function useDossier(dossierId: string): UseApiResult<DossierDetails> {
  return useApi<DossierDetails>(DossiersUrls.GET(dossierId));
}

export function useOperations(
  dossierId: string,
  searchParams: OperationsSearchParams,
): UseApiResult<Operations> {
  return useApi<Operations>(DossiersUrls.OPERATIONS_WITH_PARAMS(dossierId), {
    searchParams: searchParams as SearchParams,
  });
}

export function useOperation(
  dossierId?: string,
  operationId?: string,
): UseApiResult<OperationDetails> {
  return useApi<OperationDetails>(
    dossierId && operationId
      ? DossiersUrls.OPERATION(dossierId, operationId)
      : null,
  );
}

export const useDownloadDossiersCSV = (
  filters?: Pick<
    DossiersSearchParams,
    | 'keyword'
    | 'referentsCommerciauxEmails'
    | 'statuts'
    | 'companyId'
    | 'ficheDeLiaisonId'
    | 'dateFinChantierMin'
    | 'dateFinChantierMax'
    | 'accountId'
    | 'demandeursCee'
  >,
) => {
  return useDownload(DossiersUrls.EXPORT, {
    searchParams: filters,
  }).download;
};

export const useUploadDossierAttachmentAction = (dossierId: string): string => {
  return DossiersUrls.ATTACHMENTS(dossierId);
};

/**
 * Hook pour obtenir le code département à partir d'un code postal
 * @param postalCode - Code postal français à 5 chiffres (ex: "75001")
 * @returns UseApiResult contenant le code département ou une erreur si le code postal est invalide
 */
export function useDepartmentCodeByPostalCode(
  postalCode: string | null | undefined,
): UseApiResult<DepartmentCodeResponse> {
  return useApi<DepartmentCodeResponse>(
    postalCode && postalCode.length === 5
      ? DossiersUrls.GET_DEPARTMENT_CODE(postalCode)
      : null,
    { enabled: false, retry: false },
  );
}

export function useNouveauDossierConcessionnaireMutation() {
  return useMutation<
    { id: string; volume: number; prime: number },
    Error,
    { particulier: boolean; formData: FormData }
  >(async ({ particulier, formData }) => {
    let body;
    const response = await BaseService.request(
      particulier ? DossiersUrls.NEW_FORM_PARTICULIER : DossiersUrls.NEW_FORM,
      'POST',
      true,
      formData,
      true,
    );

    try {
      body = await response.json();

      if (!response.ok) {
        throw new Error(
          body?.message ?? t('nouveau_formulaire:errors:unknown'),
        );
      }
    } catch (error) {
      throw new Error(t('nouveau_formulaire:errors:unknown'));
    }

    return body;
  });
}

export function useCalculPrimeConcessionnaire() {
  return useMutation<
    {
      primeParVehicule: number;
      primeParGwh: number;
      volume: number;
    },
    Error,
    | CalculPrimeConcessionnaireParams
    | CalculPrimeParticulierConsessionnaireParams
  >(async (body) => {
    const response = await BaseService.request(
      body.typeClientFinal === ClientType.PERSONNE_PHYSIQUE
        ? DossiersUrls.CALCULATE_PRIME_PARTICULIER
        : DossiersUrls.CALCULATE_PRIME,
      'POST',
      true,
      body,
      false,
    );

    if (!response.ok) {
      const errorBody = await response.json().catch(
        () =>
          undefined as unknown as {
            message?: string;
            statusCode?: number;
          },
      );

      throw new Error(getCalculPrimeConcessionnaireErrorMessage(errorBody));
    }

    return await response.json();
  });
}

/**
 * Hook pour obtenir les entreprises de travaux liées au compte connecté.
 * Retourne un tableau d'entreprises
 */
export function useCompanies(enabled: boolean): UseApiResult<Company[]> {
  return useApi<Company[]>(DossiersUrls.GET_COMPANIES, {
    staleTime: 5 * 1000,
    retry: false,
    enabled,
  });
}
